import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Modal, Form, Input, Button, message } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { useUser } from '../../contexts/UserContext';
import { getApiErrorMessage } from '../../utils/apiErrors';
import { LOGIN_IDENTIFIER_RULES } from '../../utils/loginIdentifier';
import {
  EmailMfaChallengeForm,
  type EmailMfaStep,
} from '../Auth/EmailMfaChallengeForm';
import { clearPendingMfaToken } from '../../services/authTokenRefresh';
import { mfaLoginEmailHint, parseMfaResendResult, applyMfaResendFeedback } from '../../utils/mfaEmailHints';
import { registerHrefWithNext } from '../../utils/onboardingNavigation';

interface LoginDialogProps {
  visible: boolean;
  onClose: () => void;
}

export const LoginDialog: React.FC<LoginDialogProps> = ({ visible, onClose }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [mfaStep, setMfaStep] = useState<EmailMfaStep | null>(null);
  const [resendHint, setResendHint] = useState<string | null>(null);
  const [resendCooldownSeconds, setResendCooldownSeconds] = useState<number | null>(null);
  const [resendCooldownToken, setResendCooldownToken] = useState(0);
  const { login, verifyMfaOtp, resendMfaOtp } = useUser();

  const resetDialog = () => {
    form.resetFields();
    setMfaStep(null);
    setResendHint(null);
    setResendCooldownSeconds(null);
    clearPendingMfaToken();
  };

  const handleSubmit = async (values: { identifier: string; password: string }) => {
    setLoading(true);
    setResendHint(null);
    setResendCooldownSeconds(null);
    try {
      const result = await login(values.identifier.trim(), values.password);
      if (result.mfaRequired) {
        setMfaStep(result.mfaStep);
        setResendHint(mfaLoginEmailHint(result.mfaEmailSent));
        return;
      }
      message.success('Login successful!');
      resetDialog();
      onClose();
    } catch (error: unknown) {
      message.error(getApiErrorMessage(error, 'Login failed. Please check your credentials.'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (otp: string) => {
    setLoading(true);
    try {
      await verifyMfaOtp(otp);
      message.success('Login successful!');
      resetDialog();
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResendLoading(true);
    setResendHint(null);
    try {
      const result = await resendMfaOtp();
      applyMfaResendFeedback(parseMfaResendResult(result), {
        setHint: setResendHint,
        setCooldownSeconds: setResendCooldownSeconds,
        bumpCooldownToken: () => setResendCooldownToken((t) => t + 1),
      });
    } catch (error: unknown) {
      setResendCooldownSeconds(null);
      setResendHint(getApiErrorMessage(error, 'Could not resend code.'));
    } finally {
      setResendLoading(false);
    }
  };

  const handleCancel = () => {
    resetDialog();
    onClose();
  };

  return (
    <Modal
      title={mfaStep ? 'Verify your email' : 'Login'}
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={400}
      destroyOnHidden
    >
      {mfaStep ? (
        <EmailMfaChallengeForm
          key={mfaStep}
          mfaStep={mfaStep}
          loading={loading}
          resendLoading={resendLoading}
          resendHint={resendHint}
          resendCooldownSeconds={resendCooldownSeconds}
          resendCooldownToken={resendCooldownToken}
          onResendCooldownFinished={() => setResendCooldownSeconds(null)}
          onVerify={handleVerifyOtp}
          onResend={handleResend}
          onBack={() => {
            clearPendingMfaToken();
            setMfaStep(null);
            setResendHint(null);
            setResendCooldownSeconds(null);
          }}
          backLabel="Back to login"
        />
      ) : (
        <Form form={form} layout="vertical" onFinish={handleSubmit} autoComplete="off">
          <Form.Item name="identifier" label="Email or username" rules={[...LOGIN_IDENTIFIER_RULES]}>
            <Input
              prefix={<UserOutlined />}
              placeholder="Email or username"
              size="large"
              autoComplete="username"
            />
          </Form.Item>

          <Form.Item
            name="password"
            label="Password"
            rules={[
              { required: true, message: 'Please enter your password' },
              { min: 8, message: 'Password must be at least 8 characters' },
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Enter your password"
              size="large"
              autoComplete="current-password"
            />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} block size="large">
              Login
            </Button>
          </Form.Item>
          <div style={{ textAlign: 'center', marginTop: 8 }}>
            <Link to={registerHrefWithNext()} onClick={handleCancel}>
              Create an account
            </Link>
          </div>
        </Form>
      )}
    </Modal>
  );
};
